// lib/leasing/auditActions.ts
import type { DataAuditDTO } from '#types/types';
import { TRAFFIC_ENGINE_PROCEDURE } from '#lib/footfall/assetTrafficData';
import { PLAN_EVIDENCE_PROCEDURE } from '#lib/footfall/assetPlanEvidenceData';
import { TRAFFIC_SPATIAL_PROCEDURE } from '#lib/footfall/assetSpatialData';

export const READONLY_TRAFFIC_AUDIT_PROCEDURES = [TRAFFIC_ENGINE_PROCEDURE, TRAFFIC_SPATIAL_PROCEDURE, PLAN_EVIDENCE_PROCEDURE] as const;

/**
 * The audit business rules shared by the table row (AuditRenderer) and the audit page (AuditPage).
 *
 * Both surfaces used to redeclare these folds, and they had already drifted: the row treated a
 * suggested fix array as a valid fix, the page did not. Any change to which action buttons show up
 * had to be applied twice, and an omission left a button visible in the row but absent from the
 * page. They fold once, here, with no React and no presentation.
 */

export const PROPERTY_BRAND_PIPELINE_REVIEW_PROCEDURE = 'PROPERTY_BRAND_PIPELINE_REVIEW';

/** The suggested fix, only when it is a non-empty plain object. */
export const resolveSuggestedFixRecord = (
  audit: Pick<DataAuditDTO, 'suggestedFix'>
): Record<string, unknown> | null => {
  const fix = audit.suggestedFix;
  if (typeof fix !== 'object' || fix === null || Array.isArray(fix) || Object.keys(fix).length === 0) {
    return null;
  }
  return fix;
};

export const resolvePropertyIdFromAudit = (audit: Pick<DataAuditDTO, 'suggestedFix'>): string | null => {
  const fix = resolveSuggestedFixRecord(audit);
  const propertyId = typeof fix?.propertyId === 'string' ? fix.propertyId.trim() : '';
  return propertyId.length > 0 ? propertyId : null;
};

export const resolvePropertyNameFromAudit = (
  audit: Pick<DataAuditDTO, 'suggestedFix' | 'flaggedIssues'>
): string | null => {
  const fix = resolveSuggestedFixRecord(audit);
  const directName = typeof fix?.propertyName === 'string' ? fix.propertyName.trim() : '';
  if (directName.length > 0) return directName;

  const issues = Array.isArray(audit.flaggedIssues) ? audit.flaggedIssues : [];
  for (const issue of issues) {
    const match = issue.match(/for "([^"]+)"/i);
    if (match?.[1]) return match[1].trim();
  }

  return null;
};

interface AuditActionAvailability {
  canResolve: boolean;
  isPropertyBrandPipelineReview: boolean;
  hasSuggestedFix: boolean;
  canApplyFix: boolean;
  canClearData: boolean;
  canTerminate: boolean;
}

export const resolveAuditActionAvailability = (
  audit: Pick<DataAuditDTO, 'suggestedFix' | 'procedureName' | 'entityType'>
): AuditActionAvailability => {
  if (READONLY_TRAFFIC_AUDIT_PROCEDURES.some(procedure => procedure === audit.procedureName)) {
    return { canResolve: false, isPropertyBrandPipelineReview: false, hasSuggestedFix: false,
      canApplyFix: false, canClearData: false, canTerminate: false };
  }
  const fix = resolveSuggestedFixRecord(audit);
  const isPropertyBrandPipelineReview = audit.procedureName === PROPERTY_BRAND_PIPELINE_REVIEW_PROCEDURE;
  const hasSuggestedFix = Boolean(fix);
  const canTerminate = typeof fix?.action === 'string' && fix.action === 'TERMINATE_ENTITY';

  return {
    canResolve: true,
    isPropertyBrandPipelineReview,
    hasSuggestedFix,
    canApplyFix: hasSuggestedFix && !canTerminate && !isPropertyBrandPipelineReview,
    canClearData: audit.entityType === 'Brand' && /logo|website/i.test(audit.procedureName || ''),
    canTerminate,
  };
};
